import { singleton } from 'tsyringe';
import { Game } from '../../models/game.model';
import { RecommendationConditions } from './models/conditions.model';
import { RawApplication } from './models/raw-application.model';
import {
  KnownRawGlobalConfigs,
  RawGlobalConfigurations,
} from './models/raw-config.model';
import { RawGame } from './models/raw-game.model';
import { RemoteConfigurationsService } from './remote-configurations.service';
import { Application } from '../../models/application.model';
import { ExtensionId } from './models/lookups.model';
import { GlobalConfigurations } from 'app-core/src/models/global-configurations.model';
import { IRecommendationDatastoreService } from '../../interfaces/recommendation-datastore-service.interface';
import { LoggerService } from '../logger.service';
import { NonFunctionPropertyNames } from '../../types';

// -----------------------------------------------------------------------------
@singleton()
export class RecommendationDatastoreService
  implements IRecommendationDatastoreService
{
  // ---------------------------------------------------------------------------
  private readonly logger = LoggerService.getCategory(
    'RecommendationDatastoreService',
  );

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly remoteConfigsService: RemoteConfigurationsService,
  ) {}

  // ---------------------------------------------------------------------------
  public async getGame(gameId: number): Promise<Game> {
    const rawGame = await this.remoteConfigsService.getRawGame(gameId);
    if (!rawGame) {
      return null;
    }

    const game: Game = {
      id: gameId,
      name: rawGame.name,
      recommendPercent: rawGame.recommendPercent,
      shouldShowOnStart: rawGame.shouldShowOnStart,
      targetGameId: rawGame.targetGameId,
      subGameIds: rawGame?.subGameIds ?? [],
      apps: await this.getRecommendedApps(rawGame),
      useGameTriggers:
        rawGame.apps.find((app) => app?.game_events_trigger) !== undefined,
    };

    return game;
  }

  // ---------------------------------------------------------------------------
  public async getAllGames(): Promise<Game[]> {
    const rawGames = await this.remoteConfigsService.getRawGames();
    if (!rawGames) {
      return null;
    }

    const games: Game[] = [];

    for (const [key, value] of Object.entries(rawGames)) {
      const game: Game = {
        id: parseInt(key),
        name: value.name,
        recommendPercent: value.recommendPercent,
        shouldShowOnStart: value.shouldShowOnStart,
        targetGameId: value?.targetGameId,
        subGameIds: value?.subGameIds ?? [],
        apps: await this.getRecommendedApps(value),
        useGameTriggers:
          value.apps.find((app) => app?.game_events_trigger) !== undefined,
      };

      games.push(game);
    }

    return games;
  }

  // ---------------------------------------------------------------------------
  public async getApplicationForGame(
    gameId: number,
    applicationUid: string,
  ): Promise<Application> {
    const game = await this.getGame(gameId);
    return game.apps.find((app) => app.uid === applicationUid);
  }

  // ---------------------------------------------------------------------------
  public async getGlobalConfigurations(): Promise<GlobalConfigurations> {
    const rawConfigs = await this.remoteConfigsService.getRawGlobalConfigs();
    if (!rawConfigs) {
      this.logger.error('Failed to get server configurations');
      return null;
    }

    const configsMapping = this.generateGlobalConfigsMapping();

    const result = {};

    for (const [key, rawConfig] of configsMapping.entries()) {
      if (!rawConfigs[rawConfig]) {
        continue;
      }

      result[key] = this.getConfigAsNumber(rawConfigs, rawConfig);
    }

    return result as GlobalConfigurations;
  }

  // ---------------------------------------------------------------------------
  public async setEnvironment(environment: string) {
    return this.remoteConfigsService.setNewEnvironment(environment);
  }

  // ---------------------------------------------------------------------------
  public async getHostApps() {
    return this.remoteConfigsService.getHostApps();
  }

  // ---------------------------------------------------------------------------
  private async getRecommendedApps(rawGame: RawGame): Promise<Application[]> {
    const apps: Application[] = [];

    for (const rawApp of rawGame.apps) {
      const app = await this.toApp(rawApp);
      if (app) {
        apps.push(app);
      }
    }

    return apps;
  }

  // ---------------------------------------------------------------------------
  private async toApp(rawApplication: RawApplication): Promise<Application> {
    const rawAppId = rawApplication.id;

    const name = await this.remoteConfigsService.getAppName(rawAppId);
    const uid = await this.remoteConfigsService.getAppUid(rawAppId);
    const appConditions = await this.getAppSpecificConditions(rawAppId);
    const blacklistExtensionIds = await this.getBlacklistedExtIds(rawAppId);

    const app: Application = {
      name,
      uid,
      recommendWeight: rawApplication.recommend_weight,
      displayLimit: rawApplication.display_limit,
      appConditions,
      blacklistExtensionIds,
      phasingForElectron: rawApplication.phasing_for_electron,
      phasingForOverwolf: rawApplication.phasing_for_overwolf,
      gameEventTrigger: rawApplication.game_events_trigger,
      aliasApplicationDetails:
        Object.keys(rawApplication?.alias_application_details || {}).length > 0
          ? {
              landingPageUrl:
                rawApplication?.alias_application_details?.landing_page_url,
              originalApplicationId:
                rawApplication?.alias_application_details
                  ?.original_application_id,
              externalDownloadPath:
                rawApplication?.alias_application_details
                  ?.external_download_path,
            }
          : undefined,
    };

    return app;
  }

  // ---------------------------------------------------------------------------
  private async getAppSpecificConditions(rawAppId: number) {
    const remoteConfigs = this.remoteConfigsService;
    const rawAppConditions = await remoteConfigs.getSpecificConditions(
      rawAppId,
    );

    const appSpecificConditions: RecommendationConditions[] = [];

    for (const rawCondition of rawAppConditions || []) {
      const condition = await remoteConfigs.getCondition(rawCondition);
      appSpecificConditions.push(condition);
    }

    return appSpecificConditions;
  }

  // ---------------------------------------------------------------------------
  private async getBlacklistedExtIds(rawAppId: number) {
    const config = this.remoteConfigsService;

    const blacklistedApps = await config.getBlacklistExtensionIds(rawAppId);

    const blacklistApps: ExtensionId[] = [];

    for (const app of blacklistedApps || []) {
      const blacklistApp = await config.getBlacklistAppLookup(app);
      blacklistApps.push(blacklistApp);
    }

    return blacklistApps;
  }

  // ---------------------------------------------------------------------------
  private generateGlobalConfigsMapping() {
    return new Map<
      NonFunctionPropertyNames<GlobalConfigurations>,
      KnownRawGlobalConfigs
    >([
      [
        'autoDismissNotificationsInSeconds',
        KnownRawGlobalConfigs.AutoDismissNotificationsSecs,
      ],
      [
        'resetNotificationsCapPeriodInWeeks',
        KnownRawGlobalConfigs.ResetNotificationCapPeriodWeeks,
      ],
      [
        'cooldownFromPreviousNotificationInHours',
        KnownRawGlobalConfigs.CooldownFromPreviousNotificationsHours,
      ],
      [
        'cooldownFromOverwolfLaunchInMinutes',
        KnownRawGlobalConfigs.CooldownFromOverwolfLaunchMinutes,
      ],
      [
        'cooldownFromOverwolfInstallationInHours',
        KnownRawGlobalConfigs.CooldownFromOverwolfInstallationHours,
      ],
      [
        'minimumVisibleTimeToCountInSeconds',
        KnownRawGlobalConfigs.MinimumVisibleTimeToCountInSeconds,
      ],
      [
        'passiveAnalyticsPersistentPhasing',
        KnownRawGlobalConfigs.PassiveAnalyticsPersistentPhasing,
      ],
      [
        'minimumGameSessionTimeToDisplayNotificationMins',
        KnownRawGlobalConfigs.MinimumGameSessionTimeToDisplayNotificationMins,
      ],
      [
        'resetNotificationDismissPeriodWeeks',
        KnownRawGlobalConfigs.ResetNotificationDismissPeriodWeeks,
      ],
    ]);
  }

  // ---------------------------------------------------------------------------
  private getConfigAsNumber(
    rawConfigs: RawGlobalConfigurations,
    name: KnownRawGlobalConfigs,
  ): number {
    return parseInt(rawConfigs[name], 10);
  }
}
